import { useMemo, useCallback } from "react"

import { useQueries, UseQueryResult } from "@tanstack/react-query"
import axios from "axios"

import { useRegistrationData } from "hooks/api/details/use-registration-data"
import { Question, Ticket, OrderData } from "types"

interface UseOrderTicketQuestionsResult {
  questionsByTicketType: Map<number, Question[]>
  isPending: boolean
  hasError: boolean
  results: UseQueryResult<Question[], Error>[]
}

/**
 * Hook to fetch question data for tickets in an order using React Query's useQueries
 * Automatically extracts unique ticket type IDs from order data and memoizes them
 *
 * @param orderData - Order data containing ticket set with ticket type published IDs
 * @returns Object containing questionsByTicketType map, loading states, and error state
 */
export const useOrderTicketQuestions = (
  orderData: OrderData | null | undefined
): UseOrderTicketQuestionsResult => {
  const { isAdvancedFormsEnabled } = useRegistrationData()

  const ticketTypeIds: number[] = useMemo(
    () =>
      orderData?.ticket_set
        ?.map((ticket: Ticket) => ticket.ticket_type_published)
        .filter(Boolean) ?? [],
    [orderData?.ticket_set]
  )

  // Memoized combine function to prevent recreating map on every render
  const combineResults = useCallback(
    (results: UseQueryResult<Question[], Error>[]) => {
      // Build map of ticketTypeId -> questions data using combine for better performance
      const questionsByTicketType = new Map<number, Question[]>()

      results.forEach((result, index) => {
        const ticketTypeId = ticketTypeIds[index]
        if (ticketTypeId && result.data) {
          questionsByTicketType.set(ticketTypeId, result.data)
        }
      })

      const isPending = results.some((result) => result.isPending)
      const hasError = results.some((result) => result.isError)

      return {
        questionsByTicketType,
        isPending,
        hasError,
        results,
      }
    },
    [ticketTypeIds]
  )

  const queryResults = useQueries({
    queries: ticketTypeIds.map((ticketTypeId) => ({
      queryKey: ["survey-data", ticketTypeId.toString()],
      queryFn: async (): Promise<Question[]> => {
        // Production API call (commented out during development)
        const response = await axios.get(
          `/api/registration/ticket-type-published/${ticketTypeId}/survey-data/`
        )
        return response.data
      },
      enabled: !!ticketTypeId && isAdvancedFormsEnabled,
      staleTime: 10 * 60 * 1000, // Cache for 10 minutes before considering stale
    })),
    combine: combineResults,
  })

  return {
    questionsByTicketType: queryResults.questionsByTicketType,
    isPending: queryResults.isPending,
    hasError: queryResults.hasError,
    results: queryResults.results,
  }
}
